import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { cuesAPI, cueBlocksAPI } from '../../lib/api';
import { useShowStore } from '../../stores/showStore';
import RundownTable from '../rundown/RundownTable';
import { useCan } from '../../hooks/useCan';
import CueTypeBadge from '../rundown/CueTypeBadge';

// Selectable cue types. RECORD_STREAM, NOTE and BREAK are retired — none had a
// single live cue anywhere — and are no longer offered. The backend enum still
// accepts them and their colour tokens remain, so any legacy or soft-deleted row
// keeps rendering; they simply can't be chosen for a new cue. SECTION_BREAK is
// a different type and stays.
const CUE_TYPES = ['SEGMENT', 'CAMERA', 'REPLAY', 'GRAPHIC', 'AUDIO', 'VT_VOX', 'VT', 'PRESENTERS_TO_CAMERA', 'SHOW_START', 'SECTION_BREAK', 'GROUP', 'PRE_SHOW'];

// <input> types that accept no typed text, so a shortcut key pressed while one
// holds focus is a shortcut, not a keystroke meant for a field.
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image',
]);

// Parse HH:MM:SS or MM:SS or raw number → integer seconds
function parseHMS(str) {
  if (!str) return 0;
  const parts = str.trim().split(':').map(n => parseInt(n) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parseInt(str) || 0;
}

function AddCuePanel({ showId, insertAfterOrder, suggestedParentId, suggestedParentLabel, onOpenCueBlocks, onClose }) {
  const [label, setLabel] = useState('');
  const [cueType, setCueType] = useState('SEGMENT');
  const [durationSeconds, setDurationSeconds] = useState('60');
  const [script, setScript] = useState('');
  const [notes, setNotes] = useState('');
  const [parentId, setParentId] = useState(suggestedParentId || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isInsertMode = insertAfterOrder != null;

  // Escape closes the panel, exactly as Cancel does. This is where N drops an
  // operator, so backing out has to be a keystroke rather than a hunt for the
  // button. Not while saving, though: the cue is being created regardless, and
  // closing would take an error away with it.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape' || saving) return;
      e.stopPropagation();
      onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  // typeOverride is for the type chips: Enter on one selects that type AND
  // submits, and the setCueType alongside this call has not reached state yet.
  async function handleSave(e, typeOverride) {
    e.preventDefault();
    if (!label.trim()) return;
    const type = typeOverride || cueType;
    setSaving(true);
    setError('');

    try {
      const data = {
        label: label.trim(),
        cue_type: type,
        planned_duration: durationSeconds !== '' && !isNaN(parseInt(durationSeconds)) ? parseInt(durationSeconds) : 60,
        script: script.trim() || null,
        notes: notes.trim() || null,
        parent_id: type !== 'GROUP' ? (parentId || null) : null,
      };
      if (isInsertMode) data.order = insertAfterOrder + 1;
      await cuesAPI.create(showId, data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create cue');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-app-panel border-t border-app-border p-4">
      <form onSubmit={handleSave} className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-app-fg">{isInsertMode ? 'Insert Cue (below selection)' : 'New Cue'}</h3>
          <div className="flex items-center gap-3">
            {/* Insert a saved block instead of a single cue, at this same
                position. Closes the form first so the modal isn't stacked. */}
            <button
              type="button"
              onClick={onOpenCueBlocks}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-app-fg transition-colors"
              title="Insert a saved cue block here instead"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Cue blocks
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 hover:text-app-fg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-xs">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Cue label *"
              required
              autoFocus
              className="w-full bg-app-header border border-app-border rounded px-3 py-1.5 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <input
              type="number"
              value={durationSeconds}
              onChange={e => setDurationSeconds(e.target.value)}
              placeholder="Dur (secs)"
              min="0"
              className="w-full bg-app-header border border-app-border rounded px-3 py-1.5 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Group membership indicator */}
        {suggestedParentId && cueType !== 'GROUP' && (
          <div className="flex items-center gap-2 px-2 py-1 rounded bg-amber-900/20 border border-amber-800/40">
            <span className="text-xs text-amber-500">
              Will be added inside group: <strong>{suggestedParentLabel || 'Group'}</strong>
            </span>
            <button
              type="button"
              onClick={() => setParentId(prev => prev ? null : suggestedParentId)}
              className="ml-auto text-xs text-gray-500 hover:text-app-fg transition-colors"
            >
              {parentId ? 'Remove from group' : 'Add to group'}
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {CUE_TYPES.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => setCueType(type)}
              // Enter anywhere in this form adds the cue, chips included: type a
              // label, Tab to the type, Enter. Space still only selects, so a
              // type can be changed without committing.
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                setCueType(type);
                handleSave(e, type);
              }}
              className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                cueType === type
                  ? 'border-blue-600 bg-blue-900/30 text-blue-400'
                  : 'border-app-border text-gray-600 hover:border-app-border-strong'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={script}
            onChange={e => setScript(e.target.value)}
            placeholder="Script (optional)"
            className="w-full bg-app-header border border-app-border rounded px-3 py-1.5 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full bg-app-header border border-app-border rounded px-3 py-1.5 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-500 hover:text-app-fg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !label.trim()}
            className="px-5 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
          >
            {saving ? 'Adding...' : isInsertMode ? 'Insert Cue' : 'Add Cue'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Modal to pick a cue block and apply it, or save cues as a block
function CueBlockModal({ showId, insertAfterOrder, selectedIds, initialSaveScope = null, onClose }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  // saveScope: null (closed) | 'show' | 'selected'. Seeded from initialSaveScope
  // so "Save as cue block" in the bulk bar lands straight on the name field.
  const [saveScope, setSaveScope] = useState(initialSaveScope);
  const [blockName, setBlockName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasSelection = selectedIds && selectedIds.size > 0;

  useEffect(() => {
    cueBlocksAPI.list()
      .then(res => setBlocks(res.data))
      .catch(() => setError('Failed to load cue blocks'))
      .finally(() => setLoading(false));
  }, []);

  function openSave(scope) {
    setSaveScope(scope);
    setBlockName('');
    setError('');
  }

  async function handleApply(blockId) {
    try {
      const insertAtOrder = insertAfterOrder != null ? insertAfterOrder + 1 : undefined;
      await cueBlocksAPI.apply(blockId, showId, insertAtOrder);
      onClose();
    } catch {
      setError('Failed to apply cue block');
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!blockName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const cueIds = saveScope === 'selected' ? [...selectedIds] : null;
      const res = await cueBlocksAPI.save(blockName.trim(), showId, null, cueIds);
      setBlocks(prev => [res.data, ...prev]);
      setSaveScope(null);
      setBlockName('');
    } catch {
      setError('Failed to save cue block');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, name) {
    if (!confirm(`Delete cue block "${name}"?`)) return;
    try {
      await cueBlocksAPI.delete(id);
      setBlocks(prev => prev.filter(b => b.id !== id));
    } catch {
      setError('Failed to delete cue block');
    }
  }

  // Blocks arrive newest-first, which is the wrong order for finding one you
  // saved months ago. Group them by workspace and sort both levels by name, so
  // a block's position is a property of its name rather than of when it was
  // made. The workspace heading replaces the per-row badge that used to mark
  // rows from elsewhere — a heading says it once rather than on every row.
  const groups = useMemo(() => {
    const byWorkspace = new Map();
    for (const block of blocks) {
      const name = block.workspace?.name || 'Other workspace';
      if (!byWorkspace.has(name)) byWorkspace.set(name, []);
      byWorkspace.get(name).push(block);
    }
    const collate = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    return [...byWorkspace.entries()]
      .map(([name, items]) => ({ name, items: [...items].sort((a, b) => collate(a.name, b.name)) }))
      .sort((a, b) => collate(a.name, b.name));
  }, [blocks]);

  const multiWorkspace = groups.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-app-panel border border-app-border rounded-lg shadow-2xl w-[520px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
          <h2 className="text-sm font-medium text-app-fg">Cue Blocks</h2>
          <div className="flex items-center gap-2">
            {/* Save buttons */}
            {hasSelection && (
              <button
                onClick={() => openSave(saveScope === 'selected' ? null : 'selected')}
                className={`px-3 py-1 text-xs rounded transition-colors ${saveScope === 'selected' ? 'bg-blue-700 text-white' : 'bg-app-border hover:bg-app-border-strong text-gray-300'}`}
              >
                Save selected ({selectedIds.size})
              </button>
            )}
            <button
              onClick={() => openSave(saveScope === 'show' ? null : 'show')}
              className={`px-3 py-1 text-xs rounded transition-colors ${saveScope === 'show' ? 'bg-blue-700 text-white' : 'bg-app-border hover:bg-app-border-strong text-gray-300'}`}
            >
              Save whole show
            </button>
            <button onClick={onClose} className="text-gray-600 hover:text-app-fg transition-colors ml-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Save form — shown when a save scope is active */}
        {saveScope && (
          <form onSubmit={handleSave} className="px-4 py-3 border-b border-app-border bg-app-panel">
            <p className="text-xs text-gray-400 mb-2">
              {saveScope === 'selected'
                ? `Save ${selectedIds.size} selected cue${selectedIds.size !== 1 ? 's' : ''} as a reusable block:`
                : 'Save all cues from this show as a reusable block:'}
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={blockName}
                onChange={e => setBlockName(e.target.value)}
                placeholder="Block name (e.g. Football Pre-show)"
                autoFocus
                className="flex-1 bg-app-header border border-app-border rounded px-3 py-1.5 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
              />
              <button
                type="submit"
                disabled={saving || !blockName.trim()}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded transition-colors"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className="mx-4 mt-3 px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-xs">{error}</div>
        )}

        {/* Block list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-xs text-gray-600 text-center py-4">Loading…</p>
          ) : blocks.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-4">No cue blocks saved yet. Save a show or selection above to create one.</p>
          ) : (
            <div className="space-y-4">
              {groups.map(group => (
                <div key={group.name} className="space-y-2">
                  {/* One workspace: the heading would only repeat what the
                      sidebar already says, so it is left off. */}
                  {multiWorkspace && (
                    <div className="flex items-center gap-2 px-1">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{group.name}</span>
                      <span className="text-[10px] text-gray-700">{group.items.length}</span>
                      <div className="flex-1 border-t border-app-border" />
                    </div>
                  )}
                  {group.items.map(block => {
                    const cues = Array.isArray(block.cues) ? block.cues : [];
                    return (
                      <div key={block.id} className="flex items-center justify-between px-3 py-2.5 bg-app-surface rounded border border-app-border hover:border-app-border-strong group">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-app-fg font-medium truncate">{block.name}</p>
                          <p className="text-xs text-gray-600">{cues.length} cue{cues.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                          <button
                            onClick={() => handleApply(block.id)}
                            className="px-3 py-1 text-xs bg-app-border hover:bg-blue-600 text-gray-300 hover:text-white rounded transition-colors"
                          >
                            Insert
                          </button>
                          <button
                            onClick={() => handleDelete(block.id, block.name)}
                            className="p-1 text-gray-700 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 rounded"
                            title="Delete block"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProducerView({ showId, show }) {
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [insertMode, setInsertMode] = useState(false);
  const [rowInsert, setRowInsert] = useState(null); // { order, parentId, parentLabel } | null
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  // Cue-block modal open state. null = closed; otherwise
  // { order, saveScope } — `order` is the cue order to insert the block AFTER
  // (null appends), `saveScope` pre-opens the save form ('selected' | null).
  // Carrying both lets each entry point (add-cue panel, bottom row, bulk bar)
  // open the same modal aimed at the right position and mode.
  const [blockModal, setBlockModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDuration, setBulkDuration] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneInsertAfterId, setCloneInsertAfterId] = useState('__end__'); // '__end__' | null (top) | cue id
  const cues = useShowStore(s => s.cues);
  const collapsedGroups = useShowStore(s => s.collapsedGroups);
  const cueCount = cues.length;

  // A guest without production access edits text only: no adding, deleting,
  // bulk edits or reordering (see CueRow's textOnly). The ref serves the
  // keyboard listener, which is registered once.
  const canProduce = useCan('production:edit');
  const canProduceRef = useRef(canProduce);
  canProduceRef.current = canProduce;

  // Cues visible in the rundown (children of collapsed groups are hidden)
  const displayedCues = React.useMemo(
    () => cues.filter(c => !c.parent_id || !collapsedGroups.has(c.parent_id)),
    [cues, collapsedGroups]
  );

  // When a group collapses, drop its now-hidden children from the selection so a
  // stray hidden child can't be swept into a later move-to-group — UNLESS the
  // child's parent GROUP is itself selected, in which case the group is selected
  // as a unit (with its children) and they must stay (e.g. to clone the group).
  // Returns the same set when nothing changed (no render churn).
  React.useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const next = new Set();
      for (const id of prev) {
        const cue = cues.find(c => c.id === id);
        if (!cue) continue;
        const visible = !cue.parent_id || !collapsedGroups.has(cue.parent_id);
        const parentSelected = cue.parent_id && prev.has(cue.parent_id);
        if (visible || parentSelected) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [collapsedGroups, cues]);

  // Order value of the last selected cue (for insert-below)
  const insertAfterOrder = React.useMemo(() => {
    if (selectedIds.size === 0) return null;
    const selected = cues.filter(c => selectedIds.has(c.id));
    return selected.length > 0 ? Math.max(...selected.map(c => c.order)) : null;
  }, [selectedIds, cues]);

  // When inserting below a GROUP or one of its children, auto-suggest that group as parent
  const { insertParentId, insertParentLabel } = React.useMemo(() => {
    if (selectedIds.size === 0) return { insertParentId: null, insertParentLabel: null };
    const selected = cues.filter(c => selectedIds.has(c.id)).sort((a, b) => b.order - a.order);
    const last = selected[0];
    if (!last) return { insertParentId: null, insertParentLabel: null };
    if (last.cue_type === 'GROUP') {
      return { insertParentId: last.id, insertParentLabel: last.label };
    }
    if (last.parent_id) {
      const parent = cues.find(c => c.id === last.parent_id);
      return { insertParentId: last.parent_id, insertParentLabel: parent?.label || 'Group' };
    }
    return { insertParentId: null, insertParentLabel: null };
  }, [selectedIds, cues]);

  // Track last clicked cue for shift-select
  const lastSelectedIdRef = useRef(null);

  // Selecting a GROUP cue selects its children too (and deselecting releases
  // them) — even children hidden inside a collapsed group — so a group can be
  // cloned/moved/deleted as a unit. childIds returns a GROUP's children, or [].
  const childIdsOf = useCallback(
    (cueId) => cues.filter(c => c.parent_id === cueId).map(c => c.id),
    [cues],
  );

  function handleToggleSelect(id, shiftKey = false) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const cueById = new Map(cues.map(c => [c.id, c]));
      const selectWithChildren = (cueId, on) => {
        const ids = [cueId, ...(cueById.get(cueId)?.cue_type === 'GROUP' ? childIdsOf(cueId) : [])];
        ids.forEach(x => (on ? next.add(x) : next.delete(x)));
      };

      if (shiftKey && lastSelectedIdRef.current && lastSelectedIdRef.current !== id) {
        const fromIdx = displayedCues.findIndex(c => c.id === lastSelectedIdRef.current);
        const toIdx = displayedCues.findIndex(c => c.id === id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
          for (let i = start; i <= end; i++) {
            selectWithChildren(displayedCues[i].id, true);
          }
        } else {
          selectWithChildren(id, !next.has(id));
        }
      } else {
        selectWithChildren(id, !next.has(id));
      }

      lastSelectedIdRef.current = id;
      return next;
    });
  }

  function handleSelectAll() {
    if (selectedIds.size === displayedCues.length && displayedCues.length > 0) {
      setSelectedIds(new Set());
      lastSelectedIdRef.current = null;
    } else {
      setSelectedIds(new Set(displayedCues.map(c => c.id)));
      lastSelectedIdRef.current = displayedCues[displayedCues.length - 1]?.id ?? null;
    }
  }

  function openCloneModal() {
    if (!selectedIds.size) return;
    // Default: insert after the last selected cue
    const selected = cues.filter(c => selectedIds.has(c.id));
    const lastSelected = selected.sort((a, b) => b.order - a.order)[0];
    setCloneInsertAfterId(lastSelected?.id ?? '__end__');
    setShowCloneModal(true);
  }

  async function handleBulkClone() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    // Resolve insertAfterId: null = top, undefined = default (end), or a cue id
    const insertAfterId = cloneInsertAfterId === null ? null
      : cloneInsertAfterId === '__end__' ? undefined
      : cloneInsertAfterId;
    try {
      await cuesAPI.bulkClone(showId, ids, insertAfterId);
      setSelectedIds(new Set());
      setShowCloneModal(false);
    } catch (err) {
      console.error('Bulk clone failed:', err);
    }
  }

  async function handleGroupSelected() {
    // Read cues fresh from store — never use the closure snapshot for mutations
    const liveCues = useShowStore.getState().cues;
    const eligible = liveCues.filter(c => selectedIds.has(c.id) && c.cue_type !== 'GROUP')
      .sort((a, b) => a.order - b.order);
    if (eligible.length === 0) return;
    const name = window.prompt('Group name:');
    if (!name || !name.trim()) return;

    const sortedAll = [...liveCues].sort((a, b) => a.order - b.order);
    const firstIdx = sortedAll.findIndex(c => c.id === eligible[0].id);
    const prevCue = firstIdx > 0 ? sortedAll[firstIdx - 1] : null;
    const eligibleIdSet = new Set(eligible.map(c => c.id));
    const others = sortedAll.filter(c => !eligibleIdSet.has(c.id));
    const insertIdx = prevCue ? others.findIndex(c => c.id === prevCue.id) + 1 : 0;

    const { incrementPendingOps, decrementPendingOps } = useShowStore.getState();
    incrementPendingOps();

    try {
      // Create GROUP on server — fast (~200ms); gives us a real ID with no temp-ID complexity
      const res = await cuesAPI.create(showId, {
        label: name.trim(),
        cue_type: 'GROUP',
        planned_duration: 0,
        ...(prevCue ? { order: prevCue.order + 1 } : { order: 0 }),
      });
      const newGroup = res.data;

      const orderedIds = [
        ...others.slice(0, insertIdx).map(c => c.id),
        newGroup.id,
        ...eligible.map(c => c.id),
        ...others.slice(insertIdx).map(c => c.id),
      ];

      // Single atomic store update — GROUP appears and children indent in one render
      const { addCueAndPatchChildren } = useShowStore.getState();
      addCueAndPatchChildren(newGroup, eligible.map(c => ({ id: c.id, parent_id: newGroup.id })));
      setSelectedIds(new Set());

      // Fire reorder + parent_id updates in parallel — UI is already correct, this just syncs the server
      await Promise.all([
        cuesAPI.reorder(showId, orderedIds),
        Promise.all(eligible.map(c => cuesAPI.update(showId, c.id, { parent_id: newGroup.id }))),
      ]);
    } catch (err) {
      console.error('Group selected failed:', err);
    } finally {
      decrementPendingOps();
    }
  }

  async function handleBulkDelete() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} selected cue${ids.length > 1 ? 's' : ''}?`)) return;
    // Skip children whose GROUP parent is also being deleted (the parent delete cascades on the server)
    const toDelete = ids.filter(id => {
      const cue = cues.find(c => c.id === id);
      return !cue?.parent_id || !selectedIds.has(cue.parent_id);
    });
    for (const id of toDelete) {
      try { await cuesAPI.delete(showId, id); } catch (err) { console.error(err); }
    }
    setSelectedIds(new Set());
  }

  async function handleBulkDuration(e) {
    e.preventDefault();
    const seconds = Math.max(1, parseHMS(bulkDuration));
    for (const id of selectedIds) {
      try { await cuesAPI.update(showId, id, { planned_duration: seconds }); } catch (err) { console.error(err); }
    }
    setBulkDuration('');
  }

  function openAddCue() {
    setInsertMode(false);
    setShowAddPanel(true);
    setShowAddDropdown(false);
  }

  function openInsertCue() {
    setInsertMode(true);
    setRowInsert(null);
    setShowAddPanel(true);
    setShowAddDropdown(false);
  }

  // §6a row-level insert: open the panel targeting an EXACT position (after the
  // hovered cue), inheriting that cue's group so the new cue lands in the right
  // container. Works identically while live — the primary mid-show insert path.
  function openInsertAt(cue) {
    let parentId = null, parentLabel = null;
    let order = cue.order;
    if (cue.cue_type === 'GROUP') {
      if (collapsedGroups.has(cue.id)) {
        // Collapsed group: the "+" sits below the whole collapsed block, so add
        // a NON-grouped cue positioned after the group's last (hidden) child.
        const children = cues.filter(c => c.parent_id === cue.id);
        order = children.reduce((max, c) => Math.max(max, c.order), cue.order);
      } else {
        // Open group: insert INTO the group at this position.
        parentId = cue.id; parentLabel = cue.label;
      }
    } else if (cue.parent_id) {
      parentId = cue.parent_id; parentLabel = cues.find(c => c.id === cue.parent_id)?.label || 'Group';
    }
    setInsertMode(false);
    setRowInsert({ order, parentId, parentLabel });
    setShowAddPanel(true);
    setShowAddDropdown(false);
  }

  // Unified insert descriptor for the AddCuePanel: an explicit row-level target
  // (rowInsert) wins over the selection-based "insert below" mode.
  const panelInsertOrder = rowInsert ? rowInsert.order : (insertMode ? insertAfterOrder : null);
  const panelParentId = rowInsert ? rowInsert.parentId : (insertMode ? insertParentId : null);
  const panelParentLabel = rowInsert ? rowInsert.parentLabel : (insertMode ? insertParentLabel : null);

  // Delete-key handler, kept in a ref because the listener below is registered
  // once ([] deps) and would otherwise close over a stale selectedIds/cues and
  // never delete anything. Refreshed after every render.
  const deleteHotkeyRef = useRef(() => {});
  useEffect(() => {
    deleteHotkeyRef.current = () => {
      // Don't delete rows behind an open dialog — the operator's attention (and
      // any Enter they press to confirm) belongs to that dialog.
      if (showAddPanel || blockModal || showCloneModal) return;
      // No selection is a silent no-op: handleBulkDelete returns before its
      // confirm(), so an idle Delete never raises a dialog.
      handleBulkDelete();
    };
  });

  // Handle 'N' (new cue) and Delete (remove selected cues) keys
  useEffect(() => {
    function handleKey(e) {
      // Bail only for TEXT entry. A tick box is an <input> too, and clicking one
      // leaves it focused — treating that as "typing" swallowed the very Delete
      // the operator had just lined up, forcing a click-off-the-row first.
      const el = e.target;
      const tag = el.tagName.toLowerCase();
      const isTextEntry =
        tag === 'textarea' ||
        el.isContentEditable ||
        (tag === 'input' && !NON_TEXT_INPUT_TYPES.has((el.type || 'text').toLowerCase()));
      if (isTextEntry) return;
      if (!canProduceRef.current) return;
      if (e.key === 'n' || e.key === 'N') {
        // Prevent the keypress from being delivered to the input that
        // openAddCue is about to focus — otherwise the new cue's label
        // field renders pre-filled with "n".
        e.preventDefault();
        e.stopPropagation();
        openAddCue();
      }
      if (e.key === 'Delete') {
        // confirm() focuses OK by default, so Delete-then-Enter is a two-key
        // removal while Escape still cancels.
        e.preventDefault();
        e.stopPropagation();
        deleteHotkeyRef.current();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showAddDropdown) return;
    function handler() { setShowAddDropdown(false); }
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [showAddDropdown]);

  return (
    <div className="flex flex-col h-full">
      {/* Bulk action bar */}
      {canProduce && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-1.5 bg-blue-950/40 border-b border-blue-800/40 flex-shrink-0">
          <span className="text-xs text-blue-400 font-medium flex-shrink-0">
            {selectedIds.size} selected
          </span>
          <form onSubmit={handleBulkDuration} className="flex items-center gap-1.5">
            <input
              type="text"
              value={bulkDuration}
              onChange={e => setBulkDuration(e.target.value)}
              placeholder="HH:MM:SS"
              className="w-20 bg-app-sub border border-app-border-strong rounded px-2 py-0.5 text-xs text-app-fg placeholder-gray-700 focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!bulkDuration.trim()}
              className="px-2 py-0.5 text-xs bg-app-border hover:bg-app-border-strong disabled:opacity-40 text-gray-300 rounded transition-colors"
            >
              Set Duration
            </button>
          </form>
          <button
            onClick={openCloneModal}
            className="px-2 py-0.5 text-xs text-gray-400 hover:text-app-fg transition-colors"
          >
            Clone selected
          </button>
          <button
            onClick={handleGroupSelected}
            className="px-2 py-0.5 text-xs text-amber-500 hover:text-amber-300 transition-colors"
            title="Wrap selected cues in a new GROUP"
          >
            Group selected
          </button>
          <button
            onClick={() => setBlockModal({ order: insertAfterOrder, saveScope: 'selected' })}
            className="px-2 py-0.5 text-xs text-gray-400 hover:text-app-fg transition-colors"
            title="Save the selected cues as a reusable cue block, available in all your workspaces"
          >
            Save as cue block
          </button>
          <button
            onClick={handleBulkDelete}
            className="px-2 py-0.5 text-xs text-red-500 hover:text-red-400 transition-colors"
          >
            Delete selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-gray-600 hover:text-app-fg transition-colors"
            title="Clear selection"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Rundown Table */}
      <div className="flex-1 overflow-hidden">
        <RundownTable
          showId={showId}
          readOnly={false}
          textOnly={!canProduce}
          onAddCue={canProduce ? openAddCue : undefined}
          onInsertBelowSelection={openInsertCue}
          canInsertBelow={selectedIds.size > 0}
          onOpenCueBlocks={canProduce ? () => setBlockModal({ order: null, saveScope: null }) : undefined}
          onInsertAfter={openInsertAt}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onSelectAll={canProduce ? handleSelectAll : undefined}
          onDeleteSelected={handleBulkDelete}
          onClearSelection={() => setSelectedIds(new Set())}
        />
      </div>

      {/* Add / Insert Cue Panel */}
      {canProduce && showAddPanel && (
        <AddCuePanel
          showId={showId}
          insertAfterOrder={panelInsertOrder}
          suggestedParentId={panelParentId}
          suggestedParentLabel={panelParentLabel}
          onOpenCueBlocks={() => {
            // Hand the block picker the position this panel was aiming at, then
            // dismiss the panel so the two aren't stacked.
            const order = panelInsertOrder;
            setShowAddPanel(false); setInsertMode(false); setRowInsert(null);
            setBlockModal({ order, saveScope: null });
          }}
          onClose={() => { setShowAddPanel(false); setInsertMode(false); setRowInsert(null); }}
        />
      )}

      {/* Cue Block Modal */}
      {blockModal && (
        <CueBlockModal
          showId={showId}
          insertAfterOrder={blockModal.order}
          initialSaveScope={blockModal.saveScope}
          selectedIds={selectedIds}
          onClose={() => setBlockModal(null)}
        />
      )}

      {/* Clone Insert Modal */}
      {showCloneModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => setShowCloneModal(false)}
        >
          <div
            className="bg-app-panel border border-app-border rounded-lg p-5 w-96 max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-medium text-app-fg text-sm mb-1">Clone {selectedIds.size} cue{selectedIds.size !== 1 ? 's' : ''}</h3>
            <p className="text-xs text-gray-500 mb-3">Insert copies after:</p>

            <div className="flex-1 overflow-y-auto border border-app-border rounded mb-4">
              {/* Top of rundown option */}
              <button
                onClick={() => setCloneInsertAfterId(null)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-app-line transition-colors ${
                  cloneInsertAfterId === null
                    ? 'bg-blue-900/40 text-blue-300'
                    : 'text-gray-500 hover:bg-app-surface hover:text-gray-300'
                }`}
              >
                — Beginning of rundown —
              </button>

              {cues.map((cue, idx) => {
                const isSelected = selectedIds.has(cue.id);
                const isChosen = cloneInsertAfterId === cue.id;
                return (
                  <button
                    key={cue.id}
                    onClick={() => setCloneInsertAfterId(cue.id)}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-app-line last:border-0 transition-colors flex items-center gap-2 ${
                      isChosen
                        ? 'bg-blue-900/40 text-blue-300'
                        : 'text-gray-400 hover:bg-app-surface hover:text-gray-200'
                    }`}
                  >
                    <span className="text-gray-600 w-6 flex-shrink-0 text-right">{idx + 1}</span>
                    <span className={`flex-1 truncate ${isSelected ? 'text-amber-400' : ''}`}>
                      {cue.label || '(untitled)'}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] text-amber-600 flex-shrink-0">selected</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleBulkClone}
                className="flex-1 py-1.5 text-xs font-medium bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors"
              >
                Clone here
              </button>
              <button
                onClick={() => setShowCloneModal(false)}
                className="px-4 py-1.5 text-xs text-gray-500 hover:text-app-fg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
